/**
 * chat-attachments 서명 URL 캐시 — 브라우저 훅(useSignedChatAttachmentUrls)의 순수 로직.
 *
 * locarora-admin `lib/chat/chat-attachment-signer.ts` 와 **같은 내용**을 유지한다.
 *
 * - 경로별 {signedUrl, expiresAt} 캐시. 만료 `refreshMarginMs` 전부터 재서명 대상.
 * - 한 번에 최대 `SIGN_BATCH_SIZE` 경로씩 서명(createSignedUrls).
 * - 서명 실패(정책 미적용·객체 없음·네트워크) 경로는 재시도 대기 동안 원래 URL 로
 *   폴백한다 → SELECT 정책 마이그레이션보다 코드가 먼저 배포돼도(버킷이 아직 public)
 *   화면이 깨지지 않는다. 대기는 `failRetryMs` 부터 연속 실패마다 2배(최대 30분).
 * - 진행 중(in-flight) 경로는 중복 요청하지 않는다.
 */
import { chatAttachmentPath } from "./chat-attachment-url";

export const CHAT_ATTACHMENT_SIGN_TTL_SEC = 3600;
export const SIGN_BATCH_SIZE = 100;
const DEFAULT_REFRESH_MARGIN_MS = 5 * 60_000;
const DEFAULT_FAIL_RETRY_MS = 60_000;
const MAX_FAIL_RETRY_MS = 30 * 60_000;

export interface SignedUrlResult {
  path: string | null;
  signedUrl: string | null;
  error: string | null;
}

/** createSignedUrls 래퍼. 전체 실패는 throw, 경로별 실패는 error/signedUrl=null. */
export type SignManyFn = (
  paths: string[],
  expiresInSec: number,
) => Promise<SignedUrlResult[]>;

interface CacheEntry {
  url: string;
  expiresAt: number;
}

export class ChatAttachmentSigner {
  private cache = new Map<string, CacheEntry>();
  private failedUntil = new Map<string, number>();
  private failCount = new Map<string, number>();
  private inflight = new Set<string>();

  constructor(
    private readonly signMany: SignManyFn,
    private readonly now: () => number = Date.now,
    private readonly ttlSec: number = CHAT_ATTACHMENT_SIGN_TTL_SEC,
    private readonly refreshMarginMs: number = DEFAULT_REFRESH_MARGIN_MS,
    private readonly failRetryMs: number = DEFAULT_FAIL_RETRY_MS,
  ) {}

  /** 지금 서명(또는 재서명)해야 하는 경로. */
  pathsNeedingSign(paths: readonly string[]): string[] {
    const t = this.now();
    return paths.filter((p) => {
      if (this.inflight.has(p)) return false;
      const failUntil = this.failedUntil.get(p);
      if (failUntil !== undefined && failUntil > t) return false;
      const entry = this.cache.get(p);
      return !entry || entry.expiresAt - this.refreshMarginMs <= t;
    });
  }

  /** 서명해 캐시에 넣는다. 캐시가 바뀌었으면 true. 절대 throw 하지 않는다. */
  async sign(paths: readonly string[]): Promise<boolean> {
    const todo = paths.filter((p) => !this.inflight.has(p));
    if (todo.length === 0) return false;
    todo.forEach((p) => this.inflight.add(p));

    let changed = false;
    try {
      for (let i = 0; i < todo.length; i += SIGN_BATCH_SIZE) {
        const chunk = todo.slice(i, i + SIGN_BATCH_SIZE);
        const issuedAt = this.now();
        let results: SignedUrlResult[] = [];
        try {
          results = await this.signMany(chunk, this.ttlSec);
        } catch {
          results = [];
        }
        const signed = new Map<string, string>();
        results.forEach((r, idx) => {
          const key = r.path ?? chunk[idx];
          if (key && r.signedUrl && !r.error) signed.set(key, r.signedUrl);
        });
        for (const p of chunk) {
          const url = signed.get(p);
          if (url) {
            this.cache.set(p, { url, expiresAt: issuedAt + this.ttlSec * 1000 });
            this.failedUntil.delete(p);
            this.failCount.delete(p);
          } else {
            const n = (this.failCount.get(p) ?? 0) + 1;
            this.failCount.set(p, n);
            const wait = Math.min(this.failRetryMs * 2 ** (n - 1), MAX_FAIL_RETRY_MS);
            this.failedUntil.set(p, this.now() + wait);
          }
          changed = true;
        }
      }
    } finally {
      todo.forEach((p) => this.inflight.delete(p));
    }
    return changed;
  }

  /**
   * 표시용 URL.
   * - chat-attachments 가 아닌 URL(product_card 이미지·blob: 미리보기 등) → 그대로
   * - 유효한 서명이 있으면 서명 URL
   * - 서명 실패(재시도 대기 중) → 원래 URL (버킷 public 시절엔 그대로 열림)
   * - 아직 서명 전 → undefined (호출부는 자리표시자를 그린다)
   */
  resolve(url: string): string | undefined {
    const path = chatAttachmentPath(url);
    if (!path) return url;
    const t = this.now();
    const entry = this.cache.get(path);
    if (entry && entry.expiresAt > t) return entry.url;
    if (this.failedUntil.has(path)) return url;
    return undefined;
  }

  /**
   * 다음에 다시 확인해야 할 시각(ms). 대상 경로 중 가장 이른
   * "재서명 시점(만료-여유)" 또는 "실패 재시도 시점". 없으면 null.
   */
  nextCheckAt(paths: readonly string[]): number | null {
    let next: number | null = null;
    for (const p of paths) {
      if (this.inflight.has(p)) continue;
      const failUntil = this.failedUntil.get(p);
      const entry = this.cache.get(p);
      const at =
        failUntil !== undefined
          ? failUntil
          : entry
            ? entry.expiresAt - this.refreshMarginMs
            : null;
      if (at !== null && (next === null || at < next)) next = at;
    }
    return next;
  }
}
